import React, { useState } from 'react';
import { Box, Typography, Link, Button } from '@mui/material';
import { SectionVariable } from '../types';
import { useSettings } from '../Settings';
import { RechargeModal } from './RechargeModal';
import { PortabilityModal } from './PortabilityModal';
import { openPreferredAppLink, openWhatsAppChat } from '../utils/links';
import { useTranslation } from '../i18n';

interface TopBarProps {
  variables?: SectionVariable;
  name?: string;
}

export function TopBar({ variables }: TopBarProps) {
  const { settings } = useSettings();
  const { t } = useTranslation();
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [portabilityOpen, setPortabilityOpen] = useState(false);

  const texts = variables?.text || [];
  const centerText = texts[1] || t.topBar.defaultPromo;

  const whatsappNumber = settings.socials?.whatsapp || '';
  const appLinks = settings.appLinks;
  const topBarConfig = settings.topBar;
  const middlewareUrl = settings.middlewareUrl || '';

  const hideAppPromo = settings.hideApp === true || topBarConfig?.hideAppPromo === true || (!appLinks?.playStore && !appLinks?.appStore);
  const ctaText = topBarConfig?.appPromoCta || t.topBar.downloadNowCta;
  const promoText = topBarConfig?.appPromoText || centerText;

  function handleAtendimento() {
    openWhatsAppChat(whatsappNumber);
  }

  function handleCentralDeAjuda(e: React.MouseEvent) {
    e.preventDefault();
    const faqSection = document.getElementById('faq');
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function handleBaixeAgora(e: React.MouseEvent) {
    e.preventDefault();
    openPreferredAppLink(appLinks);
  }

  return (
    <>
      <Box
        className="topbar"
        sx={{
          bgcolor: 'var(--color-primary, #6B00D6)',
          color: '#fff',
          py: 0.75,
          px: { xs: 2, md: 4 },
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.75rem',
        }}
      >
        {/* Left: Atendimento + Central de Ajuda */}
        <Box display="flex" gap={2}>
          <Link
            component="button"
            onClick={handleAtendimento}
            color="inherit"
            underline="hover"
            fontSize="0.75rem"
            sx={{ cursor: 'pointer', background: 'none', border: 'none' }}
          >
            {t.topBar.customerService}
          </Link>
          <Link
            href="#faq"
            onClick={handleCentralDeAjuda}
            color="inherit"
            underline="hover"
            fontSize="0.75rem"
          >
            {t.topBar.helpCenter}
          </Link>
        </Box>

        {/* Center: App promo (hideable) */}
        {!hideAppPromo && (
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography fontSize="0.75rem" fontWeight={600}>
              {promoText}
            </Typography>
            <Button
              onClick={handleBaixeAgora}
              size="small"
              disableRipple
              sx={{
                bgcolor: 'transparent',
                color: '#00BFFF',
                fontWeight: 700,
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                p: 0,
                minWidth: 'auto',
                minHeight: 0,
                lineHeight: 1.6,
                '&:hover': {
                  bgcolor: 'transparent',
                  color: '#00D4FF',
                },
              }}
            >
              {ctaText} &nbsp;&rsaquo;
            </Button>
          </Box>
        )}

        {/* Right: Recargas + Portabilidade */}
        <Box display="flex" gap={2}>
          <Link
            component="button"
            onClick={() => setRechargeOpen(true)}
            color="inherit"
            underline="hover"
            fontSize="0.75rem"
            sx={{ cursor: 'pointer', background: 'none', border: 'none' }}
          >
            {t.topBar.recharges}
          </Link>
          <Link
            component="button"
            onClick={() => setPortabilityOpen(true)}
            color="inherit"
            underline="hover"
            fontSize="0.75rem"
            sx={{ cursor: 'pointer', background: 'none', border: 'none' }}
          >
            {t.topBar.portability}
          </Link>
        </Box>
      </Box>

      {/* Modals */}
      <RechargeModal
        open={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        middlewareUrl={middlewareUrl}
      />
      <PortabilityModal
        open={portabilityOpen}
        onClose={() => setPortabilityOpen(false)}
        middlewareUrl={middlewareUrl}
      />
    </>
  );
}
